/* Every portal read and write, as TanStack Query hooks. Keys start with the resource so a write
   can refresh what it touched (accepting a quote changes quotes, projects and invoices). */
import { ApiError, disablePush, useIdempotencyKey } from "@corecrm/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "./client";
import type {
  AcceptOut,
  Bug,
  FeatureRequest,
  PreviewOut,
  ProjectRef,
  StoryOption,
  Budget,
  Company,
  Deliverable,
  FeedbackForm,
  Impersonation,
  ImpersonationEnded,
  Invoice,
  InvoiceSummary,
  JoinDetails,
  JoinIn,
  JoinOut,
  McpAccess,
  Me,
  PlanningBoard,
  Project,
  ProjectFile,
  ProjectSummary,
  Question,
  Quote,
  QuoteSummary,
  SignInResult,
  OpenTask,
  PendingFeedback,
  Task,
  TestCase,
  TestCaseState,
  QaProgressData,
  TestEnvironment,
  TestPriority,
  TestResult,
  TestRun,
  TestRunRecorded,
  TestSession,
  TestSessionStarted,
  TokenDetails,
  WorkBoard,
} from "./types";

export const keys = {
  me: ["me"] as const,
  quotes: ["quotes"] as const,
  quote: (id: string) => ["quotes", id] as const,
  versions: (id: string) => ["quotes", id, "versions"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  tasks: (id: string) => ["projects", id, "tasks"] as const,
  planning: (id: string, phaseId: string | null) => ["projects", id, "planning", phaseId ?? ""] as const,
  board: (id: string) => ["projects", id, "board"] as const,
  openTasks: ["tasks", "open"] as const,
  budget: (id: string) => ["projects", id, "budget"] as const,
  deliverables: (projectId: string) => ["projects", projectId, "deliverables"] as const,
  deliverable: (id: string) => ["deliverables", id] as const,
  files: (projectId: string) => ["projects", projectId, "files"] as const,
  questions: ["questions"] as const,
  openQuestions: ["questions", "open"] as const,
  projectQuestions: (projectId: string) => ["questions", "project", projectId] as const,
  invoices: ["invoices"] as const,
  feedback: ["feedback"] as const,
  invoice: (id: string) => ["invoices", id] as const,
  bugs: (projectId: string) => ["projects", projectId, "bugs"] as const,
  bug: (projectId: string, id: string) => ["projects", projectId, "bugs", id] as const,
  featureRequests: (projectId: string) => ["projects", projectId, "feature-requests"] as const,
  featureRequest: (projectId: string, id: string) => ["projects", projectId, "feature-requests", id] as const,
  testCases: (projectId: string) => ["projects", projectId, "test-cases"] as const,
  testCase: (id: string) => ["test-cases", id] as const,
  testEnvironments: (projectId: string) => ["projects", projectId, "test-environments"] as const,
  testRuns: (projectId: string, caseId: string) => ["projects", projectId, "test-runs", caseId] as const,
  testSession: (id: string) => ["test-sessions", id] as const,
  mcp: ["mcp"] as const,
};

/* ---------- Claude (UX-197) ---------- */

/** Whether the client may connect Claude, the address to give it, and what's connected. */
export function useMcpAccess() {
  return useQuery({ queryKey: keys.mcp, queryFn: () => api.get<McpAccess>("/mcp") });
}

/** Disconnect every AI client the client connected. */
export function useDisconnectClaude() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ disconnected: number }>("/mcp/connections"),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.mcp }),
  });
}

/* ---------- session ---------- */

/** The signed-in client, or null when there's no portal session. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api.get<Me>("/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

function useSignedIn() {
  const qc = useQueryClient();
  return (me: Me) => {
    qc.clear();
    qc.setQueryData(keys.me, me);
  };
}

export function useSignIn() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api.post<SignInResult>("/auth/session", body),
    onSuccess: (r) => signedIn(r.user),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await disablePush(api); // the next person to use this browser doesn't get yours
      await api.del("/auth/session");
    },
    onSettled: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

/** The administrator logged in as this client (UX-152), or null: the caution banner's content. */
export function useImpersonation() {
  const me = useMe();
  return useQuery({
    queryKey: [...keys.me, "impersonation"],
    queryFn: () => api.get<Impersonation | null>("/auth/impersonation"),
    enabled: !!me.data,
    staleTime: Infinity,
  });
}

/** Return to my account: ends the administrator's session as this client and goes back to the
    staff app, where their own session is still signed in. A full page load, not a route change. */
export async function returnToMyAccount() {
  const ended = await api.post<ImpersonationEnded>("/auth/impersonation/end");
  pageLoad.goTo(ended.url);
}

/** Full page loads (tests replace it). */
export const pageLoad = { goTo: (url: string) => window.location.assign(url) };

/** The portal end of "Log in as": the one-time token from the staff app becomes this browser's
    portal session. */
export const useClaimImpersonation = () =>
  useMutation({ mutationFn: (token: string) => api.post<void>("/auth/impersonation/claim", { token }) });

/** A one-time sign-in link; `next` is the portal page it brings them back to (UX-113). */
export const useRequestSignInLink = () =>
  useMutation({
    mutationFn: (v: string | { email: string; next?: string }) =>
      api.post<void>("/auth/sign-in-link", typeof v === "string" ? { email: v } : v),
  });

/** Staff's "Preview as client" (UX-113): the record a signed preview token names. */
export const useClientPreview = (token: string) =>
  useQuery({
    queryKey: ["preview", token],
    queryFn: () => api.get<PreviewOut>("/preview", { headers: { "X-Preview-Token": token } }),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

/** While a quote page is open and visible, tell the API every 15 s (UX-109: how long it's
    read). Nothing in a staff preview, which has no view id. */
export function useViewHeartbeat(quoteId: string, viewId: string | null | undefined) {
  useEffect(() => {
    if (!viewId) return;
    const beat = () => {
      if (document.visibilityState === "visible")
        void api.post(`/quotes/${quoteId}/views/${viewId}/heartbeat`).catch(() => undefined);
    };
    const timer = window.setInterval(beat, 15_000);
    document.addEventListener("visibilitychange", beat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [quoteId, viewId]);
}

export function useVerifySignInLink() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (token: string) => api.post<SignInResult>("/auth/sign-in-link/verify", { token }),
    onSuccess: (r) => signedIn(r.user),
  });
}

export const useRequestPasswordReset = () =>
  useMutation({ mutationFn: (email: string) => api.post<void>("/auth/password-reset", { email }) });

export const useInspectSetPassword = (token: string) =>
  useQuery({
    queryKey: ["set-password", token],
    queryFn: () => api.post<TokenDetails>("/auth/set-password/inspect", { token }),
    retry: false,
    staleTime: Infinity,
  });

export const useSetPassword = () =>
  useMutation({
    mutationFn: (body: { token: string; password: string }) => api.post<void>("/auth/set-password", body),
  });

/* ---------- joining from a sign-up link ---------- */

export const useJoinDetails = (token: string) =>
  useQuery({
    queryKey: ["join", token],
    queryFn: () => api.post<JoinDetails>("/join/inspect", { token }),
    retry: false,
    staleTime: Infinity,
  });

export function useJoin() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: JoinIn) => api.post<JoinOut>("/join", body),
    onSuccess: (r) => {
      if (r.user) signedIn(r.user);
    },
  });
}

export const useRequestNewJoinLink = () =>
  useMutation({ mutationFn: (token: string) => api.post<void>("/join/new-link", { token }) });

/* ---------- quotes ---------- */

export const useQuotes = () =>
  useQuery({ queryKey: keys.quotes, queryFn: () => api.get<QuoteSummary[]>("/quotes") });

/** Opening the current version marks it Viewed, so the list is refreshed afterwards. `via`
    "email" marks the view as coming from the quote email's link (UX-109). */
export function useQuote(id: string, via?: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.quote(id),
    queryFn: async () => {
      const q = await api.get<Quote>(`/quotes/${id}`, { query: via ? { via } : undefined });
      void qc.invalidateQueries({ queryKey: keys.quotes, exact: true });
      return q;
    },
  });
}

export const useQuoteVersions = (id: string) =>
  useQuery({
    queryKey: keys.versions(id),
    queryFn: () => api.get<QuoteSummary[]>(`/quotes/${id}/versions`),
  });

/** The quote priced with the client's picks among its options (UX-100). Nothing is saved, so
    nothing is refreshed; accepting sends the same picks. */
export const usePriceQuote = (id: string) =>
  useMutation({
    mutationFn: (choices: string[]) => api.post<Quote>(`/quotes/${id}/price`, { choices }),
  });

export function useAcceptQuote(id: string) {
  const qc = useQueryClient();
  const key = useIdempotencyKey(); // a retried accept can't accept twice
  return useMutation({
    mutationFn: (body: { signer_name: string; accept_terms: boolean; choices?: string[] }) =>
      api.post<AcceptOut>(`/quotes/${id}/accept`, body, { idempotent: key }),
    onSuccess: (r) => {
      qc.setQueryData(keys.quote(id), r.quote);
      void qc.invalidateQueries({ queryKey: keys.quotes, exact: true });
      void qc.invalidateQueries({ queryKey: keys.versions(id) });
      void qc.invalidateQueries({ queryKey: keys.projects });
      void qc.invalidateQueries({ queryKey: keys.invoices });
    },
  });
}

export function useDeclineQuote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.post<Quote>(`/quotes/${id}/decline`, { reason }),
    onSuccess: (q) => {
      qc.setQueryData(keys.quote(id), q);
      void qc.invalidateQueries({ queryKey: keys.quotes, exact: true });
      void qc.invalidateQueries({ queryKey: keys.versions(id) });
    },
  });
}

/* ---------- projects ---------- */

export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: () => api.get<ProjectSummary[]>("/projects") });

export const useProject = (id: string) =>
  useQuery({ queryKey: keys.project(id), queryFn: () => api.get<Project>(`/projects/${id}`) });

export const useTasks = (id: string) =>
  useQuery({ queryKey: keys.tasks(id), queryFn: () => api.get<Task[]>(`/projects/${id}/tasks`) });

const noRetryOn404 = (failures: number, e: unknown) =>
  !(e instanceof ApiError && e.status === 404) && failures < 1;

/** The sprint plan for a phase (the current one when null). 404 unless the project shares its
    sprints, so a 404 isn't retried. */
export const usePlanning = (id: string, phaseId: string | null, showBugs = false) =>
  useQuery({
    queryKey: [...keys.planning(id, phaseId), { showBugs }],
    queryFn: () =>
      api.get<PlanningBoard>(`/projects/${id}/planning`, {
        query: {
          ...(phaseId ? { phase_id: phaseId } : {}),
          ...(showBugs ? { include_bugs: "true" } : {}),
        },
      }),
    retry: noRetryOn404,
  });

/** The active sprint's tasks by stage, or `{sprint: null}`. 404 unless the project shares its sprints. */
export const useBoard = (id: string, showBugs = false) =>
  useQuery({
    queryKey: [...keys.board(id), { showBugs }],
    queryFn: () =>
      api.get<WorkBoard>(`/projects/${id}/board`, {
        query: showBugs ? { include_bugs: "true" } : undefined,
      }),
    retry: noRetryOn404,
  });

/** Every open task assigned to you, across projects, soonest due first (for Home). */
export const useOpenTasks = () =>
  useQuery({ queryKey: keys.openTasks, queryFn: () => api.get<OpenTask[]>("/tasks/open") });

/** Mark a task assigned to you (or your team) done, or not done after all. */
export function useMarkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.post<Task>(`/tasks/${id}/${done ? "complete" : "reopen"}`),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: keys.tasks(t.project_id) });
      void qc.invalidateQueries({ queryKey: keys.openTasks });
    },
  });
}

export const useBudget = (id: string) =>
  useQuery({ queryKey: keys.budget(id), queryFn: () => api.get<Budget>(`/projects/${id}/budget`) });

export const useDeliverables = (projectId: string) =>
  useQuery({
    queryKey: keys.deliverables(projectId),
    queryFn: () => api.get<Deliverable[]>(`/projects/${projectId}/deliverables`),
  });

/** The files the team shared on a project. */
export const useProjectFiles = (projectId: string) =>
  useQuery({
    queryKey: keys.files(projectId),
    queryFn: () => api.get<ProjectFile[]>(`/projects/${projectId}/files`),
  });

/* ---------- bugs (UX-195) ---------- */

/** The project's bugs you can see; ``open`` keeps the unfinished ones. */
export const useBugs = (projectId: string, open: boolean) =>
  useQuery({
    queryKey: [...keys.bugs(projectId), { open }],
    queryFn: () =>
      api.get<Bug[]>(`/projects/${projectId}/bugs`, { query: open ? { open_only: "true" } : undefined }),
  });

export const useBug = (projectId: string, id: string) =>
  useQuery({ queryKey: keys.bug(projectId, id), queryFn: () => api.get<Bug>(`/bugs/${id}`) });

/** The stories a bug can be against: the project's, or (maintenance) an original project's. */
export const useBugStories = (projectId: string, forProjectId: string | null) =>
  useQuery({
    queryKey: [...keys.bugs(projectId), "stories", forProjectId ?? ""],
    queryFn: () =>
      api.get<StoryOption[]>(`/projects/${projectId}/bugs/story-options`, {
        query: forProjectId ? { for_project_id: forProjectId } : undefined,
      }),
  });

/** On a maintenance project: the projects it looks after. */
export const useBugOriginals = (projectId: string, enabled: boolean) =>
  useQuery({
    queryKey: [...keys.bugs(projectId), "originals"],
    queryFn: () => api.get<ProjectRef[]>(`/projects/${projectId}/bugs/originals`),
    enabled,
  });

export function useLogBug(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Bug>(`/projects/${projectId}/bugs`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.bugs(projectId) }),
  });
}

/** Confirm the fix (the bug closes) or reject it with what's still wrong (back to Fixing). */
export function useVerifyBug(projectId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { confirmed: boolean; comment?: string }) => api.post<Bug>(`/bugs/${id}/verify`, body),
    onSuccess: (b) => {
      qc.setQueryData(keys.bug(projectId, id), b);
      void qc.invalidateQueries({ queryKey: keys.bugs(projectId) });
      void qc.invalidateQueries({ queryKey: ["projects", projectId, "comments"] });
    },
  });
}

/* ---------- feature requests (UX-196) ---------- */

/** The project's feature requests you can see; ``open`` keeps the ones still being decided. */
export const useFeatureRequests = (projectId: string, open: boolean) =>
  useQuery({
    queryKey: [...keys.featureRequests(projectId), { open }],
    queryFn: () =>
      api.get<FeatureRequest[]>(`/projects/${projectId}/feature-requests`, {
        query: open ? { open_only: "true" } : undefined,
      }),
  });

export const useFeatureRequest = (projectId: string, id: string) =>
  useQuery({
    queryKey: keys.featureRequest(projectId, id),
    queryFn: () => api.get<FeatureRequest>(`/feature-requests/${id}`),
  });

/** The stories a request can be about: the project's, or (maintenance) an original project's. */
export const useFeatureRequestStories = (projectId: string, forProjectId: string | null) =>
  useQuery({
    queryKey: [...keys.featureRequests(projectId), "stories", forProjectId ?? ""],
    queryFn: () =>
      api.get<StoryOption[]>(`/projects/${projectId}/feature-requests/story-options`, {
        query: forProjectId ? { for_project_id: forProjectId } : undefined,
      }),
  });

/** On a maintenance project: the projects it looks after. */
export const useFeatureRequestOriginals = (projectId: string, enabled: boolean) =>
  useQuery({
    queryKey: [...keys.featureRequests(projectId), "originals"],
    queryFn: () => api.get<ProjectRef[]>(`/projects/${projectId}/feature-requests/originals`),
    enabled,
  });

export function useRequestFeature(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<FeatureRequest>(`/projects/${projectId}/feature-requests`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.featureRequests(projectId) }),
  });
}

/** The files on a request you can see (downloaded through the project's files). */
export const useFeatureRequestFiles = (projectId: string, id: string) =>
  useQuery({
    queryKey: [...keys.featureRequest(projectId, id), "files"],
    queryFn: () => api.get<ProjectFile[]>(`/feature-requests/${id}/files`),
  });

/* ---------- questions ---------- */

/** Everything the team is waiting on you to answer, across your projects. */
export const useOpenQuestions = () =>
  useQuery({ queryKey: keys.openQuestions, queryFn: () => api.get<Question[]>("/questions/open") });

/** A project's questions for you: waiting on you first, then the ones answered. */
export const useProjectQuestions = (projectId: string) =>
  useQuery({
    queryKey: keys.projectQuestions(projectId),
    queryFn: () => api.get<Question[]>(`/projects/${projectId}/questions`),
  });

/** One question asked of you (``initial``: as a list showed it, refreshed in the background). */
export const useQuestion = (id: string, initial?: Question) =>
  useQuery({
    queryKey: [...keys.questions, "one", id],
    queryFn: () => api.get<Question>(`/questions/${id}`),
    initialData: initial,
  });

/** Mark a question answered, or reopen one. */
/** Every project's deliverables (for what's waiting on the client across projects). */
export function useAllDeliverables(projects: ProjectSummary[] | undefined) {
  return useQueries({
    queries: (projects ?? []).map((p) => ({
      queryKey: keys.deliverables(p.id),
      queryFn: () => api.get<Deliverable[]>(`/projects/${p.id}/deliverables`),
    })),
    combine: (results) => ({
      data: results.flatMap((r) => r.data ?? []),
      isPending: results.some((r) => r.isPending),
    }),
  });
}

export const useDeliverable = (id: string) =>
  useQuery({
    queryKey: keys.deliverable(id),
    queryFn: () => api.get<Deliverable>(`/deliverables/${id}`),
  });

function useDecided() {
  const qc = useQueryClient();
  return (d: Deliverable) => {
    qc.setQueryData(keys.deliverable(d.id), d);
    void qc.invalidateQueries({ queryKey: keys.deliverables(d.project_id) });
    // an approval can make a payment milestone due
    void qc.invalidateQueries({ queryKey: keys.project(d.project_id), exact: true });
  };
}

export function useApproveDeliverable() {
  const decided = useDecided();
  return useMutation({
    mutationFn: (id: string) => api.post<Deliverable>(`/deliverables/${id}/approve`),
    onSuccess: decided,
  });
}

export function useRequestChanges() {
  const decided = useDecided();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      api.post<Deliverable>(`/deliverables/${id}/request-changes`, { comment }),
    onSuccess: decided,
  });
}

/* ---------- invoices ---------- */

/** ``enabled`` false for anyone but a billing contact, who'd get a 403 (UX-192). */
export const useInvoices = (enabled = true) =>
  useQuery({ queryKey: keys.invoices, queryFn: () => api.get<InvoiceSummary[]>("/invoices"), enabled });

export const useInvoice = (id: string) =>
  useQuery({ queryKey: keys.invoice(id), queryFn: () => api.get<Invoice>(`/invoices/${id}`) });

/* ---------- feedback (UX-044) ---------- */

/** Feedback requests not answered yet, for "Waiting on you". */
export const usePendingFeedback = () =>
  useQuery({ queryKey: keys.feedback, queryFn: () => api.get<PendingFeedback[]>("/feedback") });

/** The form behind an emailed link; works signed out. */
export const useFeedbackForm = (token: string) =>
  useQuery({
    queryKey: ["feedback", "form", token],
    queryFn: () => api.post<FeedbackForm>("/feedback/link/inspect", { token }),
    retry: false,
  });

export function useAnswerFeedback(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { score: number; comment: string | null }) =>
      api.post<FeedbackForm>("/feedback/link", { token, ...body }),
    onSuccess: (form) => {
      qc.setQueryData(["feedback", "form", token], form);
      void qc.invalidateQueries({ queryKey: keys.feedback, exact: true });
    },
  });
}

/* ---------- CoreNode's details (company settings) ---------- */

/** Public: the signed-out pages show them too. They rarely change, so fetched once a visit. */
export const useCompany = () =>
  useQuery({
    queryKey: ["company"],
    queryFn: () => api.get<Company>("/company"),
    staleTime: Infinity,
    retry: false,
  });

/* ---------- what's waiting on the client ---------- */

/** What needs the client's attention, for Home and the tab badges. A change request waiting on
    the client counts on Quotes and on Projects, as in the prototype (pCounts). */
export function useWaiting() {
  const quotes = useQuotes();
  const projects = useProjects();
  const deliverables = useAllDeliverables(projects.data);
  // Only billing contacts see invoices (UX-192); nobody else asks for them.
  const billing = useMe().data?.billing_contact ?? false;
  const invoices = useInvoices(billing);
  const feedback = usePendingFeedback();
  const openQuotes = (quotes.data ?? []).filter((q) => q.can_respond);
  const changeRequests = openQuotes.filter((q) => q.kind === "change_request");
  const toApprove = deliverables.data.filter((d) => d.can_respond);
  const toPay = (invoices.data ?? []).filter((i) => i.status === "open");
  return {
    quotes: openQuotes.filter((q) => q.kind !== "change_request"),
    changeRequests,
    deliverables: toApprove,
    invoices: toPay,
    feedback: feedback.data ?? [],
    counts: {
      quotes: openQuotes.length,
      projects: toApprove.length + changeRequests.length,
      invoices: toPay.length,
    },
    isPending:
      quotes.isPending || projects.isPending || deliverables.isPending || (billing && invoices.isPending),
    error: quotes.error ?? projects.error ?? invoices.error,
  };
}

/* ---------- testing (docs/test-suite.md section 8) ---------- */

export interface TestCaseFilters {
  environmentId?: string;
  result?: TestResult;
  /** Empty means every state. */
  state: TestCaseState[];
  priority?: TestPriority;
  mine?: boolean;
}

/** The project's client-visible test cases with their result per environment. */
export const useTestCases = (projectId: string, f: TestCaseFilters, enabled = true) =>
  useQuery({
    queryKey: [...keys.testCases(projectId), f],
    queryFn: () =>
      api.get<TestCase[]>(`/projects/${projectId}/test-cases`, {
        query: {
          environment_id: f.environmentId,
          result: f.result ? [f.result] : undefined,
          state: f.state.length ? f.state : ["active", "functionality_changed", "retired"],
          priority: f.priority,
          mine: f.mine ? true : undefined,
          limit: 500,
        },
      }),
    enabled,
  });

/** The QA progress report for the whole project, a phase or a sprint (docs/test-suite.md section 14). */
export const useQaProgress = (
  projectId: string,
  scope: "project" | "phase" | "sprint",
  scopeId: string | null,
) =>
  useQuery({
    queryKey: ["projects", projectId, "qa-progress", scope, scopeId],
    queryFn: () =>
      api.get<QaProgressData>(`/projects/${projectId}/qa-progress`, {
        query: { scope, ...(scopeId ? { scope_id: scopeId } : {}) },
      }),
    retry: noRetryOn404,
  });

export const useTestCase = (id: string) =>
  useQuery({ queryKey: keys.testCase(id), queryFn: () => api.get<TestCase>(`/test-cases/${id}`) });

export const useTestEnvironments = (projectId: string) =>
  useQuery({
    queryKey: keys.testEnvironments(projectId),
    queryFn: () =>
      api.get<TestEnvironment[]>(`/projects/${projectId}/test-environments`, { query: { limit: 500 } }),
  });

export const useTestEnvironment = (id: string) =>
  useQuery({
    queryKey: ["test-environments", id],
    queryFn: () => api.get<TestEnvironment>(`/test-environments/${id}`),
  });

/** Your own sessions that are still open. */
export const useOpenTestSessions = (projectId: string, enabled = true) =>
  useQuery({
    queryKey: ["projects", projectId, "test-sessions", "open"],
    queryFn: () =>
      api.get<TestSession[]>(`/projects/${projectId}/test-sessions`, { query: { status: ["in_progress"] } }),
    enabled,
  });

/** What was recorded in one session. */
export const useSessionRuns = (projectId: string, sessionId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "test-runs", "session", sessionId],
    queryFn: () =>
      api.get<TestRun[]>(`/projects/${projectId}/test-runs`, { query: { session_id: sessionId } }),
  });

/** A case's run history. */
export const useTestRuns = (projectId: string, caseId: string) =>
  useQuery({
    queryKey: keys.testRuns(projectId, caseId),
    queryFn: () => api.get<TestRun[]>(`/projects/${projectId}/test-runs`, { query: { case_id: caseId } }),
  });

type QC = ReturnType<typeof useQueryClient>;

/** Refresh what a recorded run or a finished session changed. Returned, so a mutation stays pending
    until the lists are current (a second click cannot record the same test twice). */
function refreshTests(qc: QC, projectId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: keys.testCases(projectId) }),
    qc.invalidateQueries({ queryKey: keys.testEnvironments(projectId) }),
    qc.invalidateQueries({ queryKey: ["test-cases"] }),
    qc.invalidateQueries({ queryKey: ["projects", projectId, "test-runs"] }),
    qc.invalidateQueries({ queryKey: ["projects", projectId, "test-sessions"] }),
    qc.invalidateQueries({ queryKey: keys.bugs(projectId) }),
  ]);
}

/** After a refusal (403, 404, 409) the screen is stale: read the session, tests and runs again. */
function refreshAfterRefusal(
  qc: QC,
  projectId: string,
  sessionId: string,
  error: unknown,
  statuses: number[] = [403, 404, 409],
) {
  if (!(error instanceof ApiError) || !statuses.includes(error.status)) return undefined;
  return Promise.all([
    qc.invalidateQueries({ queryKey: keys.testSession(sessionId) }),
    // The environment too: a reset step added (or a step removed) since the screen opened.
    qc.invalidateQueries({ queryKey: ["test-environments"] }),
    refreshTests(qc, projectId),
  ]);
}

export function useStartTestSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (environmentId: string) =>
      api.post<TestSessionStarted>(`/projects/${projectId}/test-sessions`, {
        environment_id: environmentId,
      }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["projects", projectId, "test-sessions"] }),
        qc.invalidateQueries({ queryKey: keys.testEnvironments(projectId) }),
      ]),
    // A refusal (409: a session is already open there) means the lists are stale.
    onError: () => void qc.invalidateQueries({ queryKey: keys.testEnvironments(projectId) }),
  });
}

export const useTestSession = (id: string) =>
  useQuery({ queryKey: keys.testSession(id), queryFn: () => api.get<TestSession>(`/test-sessions/${id}`) });

export function useTickTestSteps(projectId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: "setup" | "reset"; step_ids: string[] }) =>
      api.post<TestSession>(`/test-sessions/${sessionId}/ticks`, body),
    onSuccess: (s) => qc.setQueryData(keys.testSession(sessionId), s),
    // 400 too: a step id that no longer exists on the environment.
    onError: (e) => refreshAfterRefusal(qc, projectId, sessionId, e, [400, 403, 404, 409]),
  });
}

export interface RunBody {
  case_id: string;
  result: TestResult;
  notes?: string;
  /** A Fail logs a bug unless this is false (the contact may not log bugs, or the project is closed). */
  create_bug?: boolean;
  /** With Blocked: an existing bug that blocks the test. */
  blocking_bug_id?: string;
}

export function useRecordTestRun(projectId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RunBody) => api.post<TestRunRecorded>(`/test-sessions/${sessionId}/runs`, body),
    onSuccess: () => refreshTests(qc, projectId),
    onError: (e) => refreshAfterRefusal(qc, projectId, sessionId, e),
  });
}

/** Complete the session, or abandon it with a note. */
export function useFinishTestSession(projectId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (abandonNote?: string) =>
      abandonNote === undefined
        ? api.post<TestSession>(`/test-sessions/${sessionId}/complete`)
        : api.post<TestSession>(`/test-sessions/${sessionId}/abandon`, { note: abandonNote }),
    onSuccess: (s) => {
      qc.setQueryData(keys.testSession(sessionId), s);
      return refreshTests(qc, projectId);
    },
    onError: (e) => refreshAfterRefusal(qc, projectId, sessionId, e),
  });
}
